import Quill from 'quill'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { emitSpeakerNotes } from '../../../Socket/Socket';
import { QUILL_FONT_PICKER, registerQuillFontWhitelist } from '../../../utils/quillFonts';
import { slideAPI } from '../../../store/api';
import { slideAction } from '../../../store/slideSlice';
import classes from './speakerNotes.module.css';
import { getUpdatedSlides } from '../../../utils/helper';

// The bar is a fixed overlay measured in viewport height, the same unit the
// canvas padding and the chat launcher use to clear it.
const DEFAULT_HEIGHT_VH = 8;
const MIN_HEIGHT_VH = 5;
const MAX_HEIGHT_VH = 55;
const HEIGHT_STORAGE_KEY = 'speakerNotesHeightVh';

const clampHeight = (value) =>
  Math.min(MAX_HEIGHT_VH, Math.max(MIN_HEIGHT_VH, value));

const readStoredHeight = () => {
  try {
    const stored = parseFloat(window.localStorage.getItem(HEIGHT_STORAGE_KEY));
    return Number.isFinite(stored) ? clampHeight(stored) : DEFAULT_HEIGHT_VH;
  } catch (error) {
    return DEFAULT_HEIGHT_VH;
  }
};

const persistHeight = (value) => {
  try {
    window.localStorage.setItem(HEIGHT_STORAGE_KEY, String(value));
  } catch (error) {
    /* storage can be unavailable (private windows, blocked cookies) */
  }
};

// Word count straight off the Delta, so it stays right for both the local
// draft and content arriving from a collaborator.
const countWords = (delta) => {
  const ops = Array.isArray(delta) ? delta : delta?.ops;
  if (!Array.isArray(ops)) return 0;
  const text = ops
    .map((op) => (typeof op.insert === 'string' ? op.insert : ' '))
    .join('');
  return text.trim().split(/\s+/).filter(Boolean).length;
};

const NotesGlyph = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
    <path
      d="M8 3.5h8a2.5 2.5 0 0 1 2.5 2.5v12A2.5 2.5 0 0 1 16 20.5H8A2.5 2.5 0 0 1 5.5 18V6A2.5 2.5 0 0 1 8 3.5Z"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="M9 8.5h6M9 12h6M9 15.5h3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ChevronGlyph = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
    <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const SpeakerNotes = () => {

  const Parchment = Quill.import('parchment');
  const lineHeightArr = [
    '1.0',
    '1.2',
    '1.5',
    '1.6',
    '1.8',
    '2.0',
    '2.4',
    '2.8',
    '3.0',
    '4.0',
    '5.0'
  ]
    const lineHeightConfig = {
      scope: Parchment.Scope.INLINE,
      whitelist: lineHeightArr
    };

    const lineHeightClass = new Parchment.Attributor.Class('lineheight', 'ql-line-height', lineHeightConfig);
    const lineHeightStyle = new Parchment.Attributor.Style('lineheight', 'line-height', lineHeightConfig);
    Parchment.register(lineHeightClass);
    Parchment.register(lineHeightStyle);

const fontFamilyArr = QUILL_FONT_PICKER;
registerQuillFontWhitelist(Quill);

const fontSizeArr = ['10px', '11px', '12px', '14px','16px', '18px','20px','22px', '24px','26px','28px','30px', '32px','34px', '36px','38px','40px','42px','44px','46px','48px','50px','52px','54px','56px','58px','60px','62px','64px','72px', '96px'];
var Size = Quill.import('attributors/style/size');
Size.whitelist = fontSizeArr;
Quill.register(Size, true);

    
const toolbarConfig = [
  ["bold", "italic", "underline", "strike"], // toggled buttons
  ["link"],
  [{'lineheight': lineHeightArr}],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ script: "sub" }, { script: "super" }], // superscript/subscript
  [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
  [{ direction: "rtl" }], // text direction
  [{ 'size': fontSizeArr }], // custom dropdown
  // [{ size: ["small", false, "large", "huge"] }], // custom dropdown
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  
  [{ color: [] }, { background: [] }], // dropdown with defaults from theme
  [{ 'font': fontFamilyArr }],
  [{ align: [] }],
  
  ["clean"], // remove formatting button
];

  const {updateSpeakerNotesInDatabase} = slideAPI;

    const {id} = useParams();
    const [deltaState, setDeltaState] = useState();
    const quillRef = useRef();
    const dispatch = useDispatch();
    const currentSlideId = useSelector(state => state.slide.currentSlideId);
    const [checkCurrentSlideId, setCurrentSlideId] = useState();
    const speakerNotes = useSelector(state => currentSlideId && state.slide.slides[currentSlideId]?.speakerNotes);
    const isReadOnly = useSelector((state)=>state.slide.isReadOnly);
    const existingSlides = useSelector((state) => state.slide.slides);
    const mirrorSlides = useSelector((state) => state.slide.mirrorSlides);

    const containerRef = useRef(null);
    const [heightVh, setHeightVh] = useState(readStoredHeight);
    const [isCollapsed, setIsCollapsed] = useState(false);
    // Local save feedback: the global "Saving..." chip fires for every element on
    // the deck, so the bar tracks its own edits instead.
    const [saveState, setSaveState] = useState('idle');

    let toolbar;
    useEffect(() => {

      if(currentSlideId && currentSlideId !== checkCurrentSlideId){
        let globalToolbarContainer = document.querySelector('#quillToolbar');

        const notes = new Quill(`#notesQuill`, {
            theme: "snow",
            placeholder: "Add speaker notes for this slide…",
            modules: {toolbar: toolbarConfig},
          })
          quillRef.current = notes;
          notes.setContents(speakerNotes, "listener");
          setCurrentSlideId(currentSlideId);
          notes.on('text-change', function(delta, oldDelta, source){
            if(source === "listener") {
              notes.setSelection(null);
              return
            }
            !notes.getText().includes(" ") && notes.container.addEventListener('keydown',function(e){
              if(e.key === " " || e.key === "enter"){   //capitalize first letter if textbox is empty
                let firstWord = notes.editor.delta.ops[0].insert.split('');
                let firstChar = firstWord[0].toUpperCase();
                firstWord[0] = firstChar;
                // firstWord[firstWord.length] = " ";
                notes.deleteText(0, firstWord.length-1);
                notes.insertText(0,firstWord.join(''));
                notes.deleteText(firstWord.length-1,1);
              }
          },{once: true});
              setDeltaState(notes.getContents())
            });
          notes.on('selection-change', function(range){
            if(range){
              // dispatch(slideAction.setCurrentElement('notesQuill'))
              globalToolbarContainer.appendChild(toolbar.container);
              toolbar.container.addEventListener('mousedown', function(e){
                e.preventDefault();
              },false);
              // showToolbar(toolbar.container);
            }else if(!notes.hasFocus){
              globalToolbarContainer.children.length !== 0 && globalToolbarContainer.removeChild(toolbar.container);
              // hideToolbar(toolbar.container);
            }
          });
          toolbar = notes.getModule('toolbar');
          globalToolbarContainer.appendChild(toolbar.container);
          globalToolbarContainer.removeChild(toolbar.container);
        }
        else if(currentSlideId && currentSlideId === checkCurrentSlideId && !quillRef.current.getSelection()){
          // console.log("set content runs for emiter");
            quillRef.current.setContents(speakerNotes, "listener");
            // quillRef.current.setSelection(null);
        }
         
    }, [currentSlideId, speakerNotes]);

    useEffect(() => {
      if(deltaState && deltaState !== speakerNotes){
        dispatch(slideAction.updateIsSaving({ isSaving: 'Saving...', lastSavedDateTime: '' }));
        setSaveState('saving');
      }
        let interval = setTimeout(() =>{
          if(deltaState === speakerNotes) return
          if(deltaState){
            emitSpeakerNotes({roomId: id, notesData: {slideId: currentSlideId, content: deltaState}});
            dispatch(slideAction.addSpeakerNotes({slideId: currentSlideId, content: deltaState}));
            const updatedSlides = getUpdatedSlides({ slideId: currentSlideId, content: deltaState }, 'speakerNotes', existingSlides);
            const data = {
              slides: updatedSlides,
              mirrorSlides: mirrorSlides
            }
            updateSpeakerNotesInDatabase(id, currentSlideId, deltaState,dispatch,data);
            setSaveState('saved');
          }
        }, 500);
        return () => {
            clearTimeout(interval)
        }
    }, [deltaState]);

    // "Saved" is a confirmation, not a permanent label — fade it back out.
    useEffect(() => {
      if (saveState !== 'saved') return undefined;
      const timer = setTimeout(() => setSaveState('idle'), 2200);
      return () => clearTimeout(timer);
    }, [saveState]);

    const onBlurHandler = () =>{
      let globalToolbarContainer = document.querySelector('#quillToolbar');
      globalToolbarContainer.children.length !== 0 &&  globalToolbarContainer.children[0].remove();
    }

    const applyHeight = useCallback((value) => {
      const next = clampHeight(value);
      setHeightVh(next);
      persistHeight(next);
    }, []);

    // Drag to resize. Measured against window.innerHeight (the box the bar
    // actually lives in) rather than window.screen.height, so the handle tracks
    // the pointer in a windowed or scaled browser too.
    const handleResizeStart = (event) => {
      if (isCollapsed || event.button !== 0) return;
      const container = containerRef.current;
      if (!container) return;
      event.preventDefault();

      const startY = event.clientY;
      const startHeight = container.getBoundingClientRect().height;
      const viewportHeight = window.innerHeight;
      let nextHeight = heightVh;

      const handlePointerMove = (moveEvent) => {
        const raw = ((startHeight + (startY - moveEvent.clientY)) / viewportHeight) * 100;
        nextHeight = clampHeight(raw);
        container.style.height = `${nextHeight}vh`;
      };
      const handlePointerUp = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        document.body.style.removeProperty('user-select');
        applyHeight(nextHeight);
      };

      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    };

    const handleResizeKeyDown = (event) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        applyHeight(heightVh + 2);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        applyHeight(heightVh - 2);
      } else if (event.key === 'Home') {
        event.preventDefault();
        applyHeight(DEFAULT_HEIGHT_VH);
      }
    };

    const slideNumber = useMemo(() => {
      const slideIds = Object.keys(existingSlides || {}).filter(
        (slideId) => !slideId.includes('master')
      );
      const index = slideIds.indexOf(currentSlideId);
      return index >= 0 ? index + 1 : null;
    }, [existingSlides, currentSlideId]);

    const wordCount = useMemo(
      () => countWords(deltaState || speakerNotes),
      [deltaState, speakerNotes]
    );

    const pointerEventsStyle = {
      pointerEvents: isReadOnly? "none" : "all"
    };

    return (
      <>
        {currentSlideId && (
          <div
            id="speakerNoteContainer"
            ref={containerRef}
            className={`${classes.speakerNotesContainer} ${isCollapsed ? classes.collapsed : ''}`}
            style={{ ...pointerEventsStyle, height: isCollapsed ? 'auto' : `${heightVh}vh` }}
          >
            <div
              className={classes.resizer}
              onPointerDown={handleResizeStart}
              onDoubleClick={() => applyHeight(DEFAULT_HEIGHT_VH)}
              onKeyDown={handleResizeKeyDown}
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize speaker notes"
              title="Drag to resize · double-click to reset"
              tabIndex={0}
            >
              <span className={classes.grip} aria-hidden="true" />
            </div>

            <div className={classes.bar}>
              <span className={classes.barIcon} aria-hidden="true">
                <NotesGlyph />
              </span>
              <span className={classes.barTitle}>Speaker notes</span>
              {slideNumber && <span className={classes.slideChip}>Slide {slideNumber}</span>}

              <span className={classes.barSpacer} />

              {saveState !== 'idle' && (
                <span
                  className={`${classes.saveState} ${saveState === 'saving' ? classes.saving : classes.saved}`}
                >
                  {saveState === 'saving' ? 'Saving…' : 'Saved'}
                </span>
              )}
              {wordCount > 0 && (
                <span className={classes.wordCount}>
                  {wordCount} {wordCount === 1 ? 'word' : 'words'}
                </span>
              )}
              <button
                type="button"
                className={classes.collapseButton}
                onClick={() => setIsCollapsed((collapsed) => !collapsed)}
                aria-expanded={!isCollapsed}
                aria-label={isCollapsed ? 'Expand speaker notes' : 'Collapse speaker notes'}
                title={isCollapsed ? 'Expand speaker notes' : 'Collapse speaker notes'}
              >
                <span className={`${classes.collapseGlyph} ${isCollapsed ? classes.collapseGlyphUp : ''}`}>
                  <ChevronGlyph />
                </span>
              </button>
            </div>

            <div className={classes.editorWrap}>
              <div tabIndex="0" id="notesQuill" className={classes.editor} onBlur={onBlurHandler}></div>
            </div>
          </div>
        )}
      </>
    )
}
